import type { ScheduleItem } from '../types/index.js'
import { broadcastDayWindow } from './broadcast-day.js'
import { scheduleItemTitle } from './utils.js'

/**
 * Chevauchements de la journée affichée, calculés depuis les éléments déjà en
 * mémoire — comme les trous (`lib/grid-gaps.ts`). La barre qui les annonce est
 * donc toujours juste : elle disparaît d'elle-même dès que la grille rechargée
 * n'en contient plus, sans notification à fermer.
 *
 * Aucun chevauchement n'est voulu dans une grille d'antenne : tout recouvrement
 * d'au moins une seconde est signalé.
 *
 * Jumeau de `backend/src/lib/realign-overlaps.ts` (appariement) et de
 * `backend/src/lib/duplicate-placement.ts` (doublon) : mêmes critères, pour que
 * la barre annonce exactement ce que le recalage serveur verra.
 */

/** Un élément dans ces états occupe encore l'antenne. */
const LIVE_STATUSES = new Set(['SCHEDULED', 'LOADING', 'ON_AIR'])
const HABILLAGE_ITEM_TYPES = new Set(['JINGLE', 'INTERLUDE', 'FILLER'])

export type OverlapKind = 'SHIFTABLE' | 'DUPLICATE' | 'FROZEN'

export interface GridOverlap {
  /** Clé stable de la paire, pour la navigation et le rendu. */
  key:           string
  /** L'élément qui déborde. */
  overItemId:    string
  overItemTitle: string
  /** L'élément recouvert, celui que le recalage déplace. */
  itemId:        string
  itemTitle:     string
  gridId:        string
  /** Début du recouvrement = début de l'élément recouvert. */
  startTime:     string
  /** Fin du recouvrement. */
  endTime:       string
  overlapSec:    number
  /**
   * `SHIFTABLE` se recale ; `DUPLICATE` se retire à la main (l'enchaîner le
   * diffuserait deux fois) ; `FROZEN` est déjà chargé ou à l'antenne.
   */
  kind:          OverlapKind
}

/** Miroir de `isDuplicatePlacement` : même contenu posé deux fois au même endroit. */
export function isDuplicatePlacement(
  left: Pick<ScheduleItem, 'itemType' | 'programId' | 'sortOrder'>,
  right: Pick<ScheduleItem, 'itemType' | 'programId' | 'sortOrder'>,
): boolean {
  if (HABILLAGE_ITEM_TYPES.has(left.itemType) || HABILLAGE_ITEM_TYPES.has(right.itemType)) {
    return HABILLAGE_ITEM_TYPES.has(left.itemType)
      && HABILLAGE_ITEM_TYPES.has(right.itemType)
      && left.sortOrder === right.sortOrder
  }
  return left.programId !== null && left.programId === right.programId
}

/**
 * Paires qui se recouvrent réellement, dont l'élément recouvert commence dans la
 * journée `date`. Deux éléments bord à bord ne se chevauchent pas ; un
 * recouvrement de moins d'une seconde est un arrondi, pas une faute.
 */
export function findGridOverlaps(items: ScheduleItem[], date: string): GridOverlap[] {
  const { from, to } = broadcastDayWindow(date)
  const dayStart = from.getTime()
  const dayEnd = to.getTime()

  const sorted = items
    .filter(item => LIVE_STATUSES.has(item.status))
    .map(item => ({ item, start: new Date(item.startTime).getTime(), end: new Date(item.endTime).getTime() }))
    .sort((a, b) => a.start - b.start || a.end - b.end || a.item.sortOrder - b.item.sortOrder)

  // Les deux exemplaires d'un doublon qui se recouvrent. Tant que l'un n'est
  // pas retiré, tout recouvrement qui en touche un se règle en le retirant, pas
  // en recalant : le recalage laisse un doublon en place
  // (`overlap-shift-plan.ts`), la barre ne doit donc pas le proposer.
  const extraCopies = new Set<string>()
  for (let i = 0; i < sorted.length; i++) {
    for (let j = i + 1; j < sorted.length && sorted[j].start < sorted[i].end; j++) {
      if (isDuplicatePlacement(sorted[i].item, sorted[j].item)) {
        extraCopies.add(sorted[i].item.id)
        extraCopies.add(sorted[j].item.id)
      }
    }
  }

  const overlaps: GridOverlap[] = []
  for (let i = 0; i < sorted.length; i++) {
    const over = sorted[i]
    for (let j = i + 1; j < sorted.length; j++) {
      const covered = sorted[j]
      if (covered.start >= over.end) break
      if (covered.start < dayStart || covered.start >= dayEnd) continue
      const endMs = Math.min(over.end, covered.end)
      const overlapSec = Math.round((endMs - covered.start) / 1000)
      if (overlapSec <= 0) continue

      const kind: OverlapKind = extraCopies.has(covered.item.id) || isDuplicatePlacement(over.item, covered.item)
        ? 'DUPLICATE'
        : covered.item.status === 'SCHEDULED' ? 'SHIFTABLE' : 'FROZEN'

      overlaps.push({
        key:           `${over.item.id}>${covered.item.id}`,
        overItemId:    over.item.id,
        overItemTitle: scheduleItemTitle(over.item, 'Élément') ?? 'Élément',
        itemId:        covered.item.id,
        itemTitle:     scheduleItemTitle(covered.item, 'Élément') ?? 'Élément',
        gridId:        covered.item.gridId,
        startTime:     new Date(covered.start).toISOString(),
        endTime:       new Date(endMs).toISOString(),
        overlapSec,
        kind,
      })
    }
  }
  return overlaps
}

/** « 42 s », « 3 min », « 3 min 12 s » — l'ampleur d'un débordement. */
export function formatOverlapAmount(overlapSec: number): string {
  const minutes = Math.floor(overlapSec / 60)
  const seconds = overlapSec % 60
  if (minutes === 0) return `${seconds} s`
  return seconds > 0 ? `${minutes} min ${seconds} s` : `${minutes} min`
}
